import React, { useEffect, useState } from "react";
import { Button, Spin, Typography, Modal } from "antd";
import {
  PlayCircleOutlined,
  ReloadOutlined,
  LogoutOutlined,
  ReadOutlined,
  HistoryOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import "./exam.css";
const { Title, Text } = Typography;
const EXAM_API_BASE =
  process.env.REACT_APP_EXAM_API_BASE_URL ||
  "http://localhost:5000/api/exams";
export default function ExamPage() {
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [exams, setExams] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [attemptHistory, setAttemptHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const exam = exams.find((item) => item.level === selectedLevel) || null;
  const departmentName =
    exam?.department_name ||
    exams[0]?.department_name ||
    "Department name not found";
  const userId = localStorage.getItem("exam_user_id");
  const deptId = localStorage.getItem("exam_dept_id");
  const unitCode = localStorage.getItem("exam_unit_code") || "";
  const unitName = localStorage.getItem("exam_unit_name") || "";
  const examUserData = (() => {
    try {
      return JSON.parse(localStorage.getItem("examUserData") || "{}");
    } catch {
      return {};
    }
  })();
  const loginUser = examUserData?.data || {};
  // Login API fields:
  // su_id     -> internal backend user_id
  // username  -> displayed User ID
  // full_name -> displayed employee name
  const fullName =
    loginUser?.full_name ||
    loginUser?.staff_name ||
    loginUser?.name ||
    loginUser?.employee_name ||
    loginUser?.username ||
    userId ||
    "Employee";
  const username =
    loginUser?.username ||
    loginUser?.emp_code ||
    loginUser?.staff_code ||
    userId ||
    "-";
  const employeeName = fullName;
  const employeeCode = username;
  useEffect(() => {
    if (!userId || !deptId || !unitCode || !unitName) {
      window.location.href = "/test-login";
      return;
    }
    loadActiveExam();
    loadAttemptHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const loadActiveExam = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${EXAM_API_BASE}/active?user_id=${encodeURIComponent(
          userId
        )}&dept_id=${encodeURIComponent(deptId)}`
      );
      const result = await response.json();
      if (
        result?.status &&
        Array.isArray(result?.data) &&
        result.data.length > 0
      ) {
        const availableExams = result.data;
        setExams(availableExams);
        // Keep all level buttons visible, but do not auto-select any level.
        // Assessment details appear only after the user chooses a level.
        setSelectedLevel(null);
      } else {
        setExams([]);
        setSelectedLevel(null);
      }
    } catch (error) {
      console.error("Active exam error:", error);
      setExams([]);
      setSelectedLevel(null);
    } finally {
      setLoading(false);
    }
  };
  const loadAttemptHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await fetch(
        `${EXAM_API_BASE}/my-results?user_id=${encodeURIComponent(userId)}`
      );
      const result = await response.json();
      if (
        result?.status &&
        Array.isArray(result?.data)
      ) {
        setAttemptHistory(result.data);
      } else {
        setAttemptHistory([]);
      }
    } catch (error) {
      console.error("Attempt history error:", error);
      setAttemptHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };
  const formatAttemptDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "-";
    }
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };
  const formatLevel = (level) => {
    if (!level) return "-";
    return String(level)
      .replaceAll("_", " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  };
  const startExamApi = async () => {
    if (!exam?.exam_id) return;
    try {
      setStarting(true);
      const response = await fetch(
        `${EXAM_API_BASE}/${exam.exam_id}/start`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: Number(userId),
            dept_id: Number(deptId),
            unit_code: unitCode,
            unit_name: unitName,
            full_name: fullName,
            username: username,
          }),
        }
      );
      const result = await response.json();
      if (!result?.status) {
        Modal.error({
          title: "Unable to Start Test",
          content: result?.message || "The test could not be started.",
        });
        return;
      }
      const attemptId = result?.data?.attempt_id;
      if (!attemptId) {
        Modal.error({
          title: "Unable to Start Test",
          content: "Attempt ID was not returned by the server.",
        });
        return;
      }
      localStorage.setItem("exam_attempt_id", String(attemptId));
      localStorage.setItem(
        "current_exam",
        JSON.stringify({
          exam_id: exam.exam_id,
          exam_name: exam.exam_name,
          level: exam.level,
          attempt_id: attemptId,
          attempt_no: result?.data?.attempt_no || 1,
          duration_minutes:
            result?.data?.duration_minutes || exam.duration_minutes || 30,
          started_at: result?.data?.started_at || null,
          expires_at: result?.data?.expires_at || null,
          questions: result?.data?.questions || [],
        })
      );
      window.location.href = `/exam/test/${exam.exam_id}`;
    } catch (error) {
      console.error("Start exam error:", error);
      Modal.error({
        title: "Unable to Start Test",
        content: "Something went wrong while starting the test.",
      });
    } finally {
      setStarting(false);
    }
  };
  const handleStartExam = () => {
    if (!exam?.exam_id) return;
    Modal.confirm({
      title:
        Number(exam.submitted_attempts || 0) > 0
          ? "Retake Assessment?"
          : "Start Assessment?",
      content: (
        <div className="exam-modal-copy">
          <p>Level: <strong>{formatLevel(exam.level)}</strong></p>
          <p>The timer starts immediately when you begin.</p>
          <p>
            <strong>{exam.question_count}</strong> questions
            <span className="exam-dot">•</span>
            <strong>{exam.duration_minutes || 30} minutes</strong>
          </p>
        </div>
      ),
      okText:
        Number(exam.submitted_attempts || 0) > 0
          ? "Start Retake"
          : "Start Test",
      cancelText: "Cancel",
      onOk: startExamApi,
    });
  };
  const handleLogout = () => {
    [
      "examUserData",
      "exam_user_id",
      "exam_dept_id",
      "exam_unit_code",
      "exam_unit_name",
      "exam_attempt_id",
      "current_exam",
      "exam_answers",
      "exam_result",
    ].forEach((key) => localStorage.removeItem(key));
    window.location.href = "/test-login";
  };
  if (loading) {
    return (
      <div className="exam-loading-page">
        <Spin size="large" />
        <div className="exam-loading-text">Loading assessment...</div>
      </div>
    );
  }
  return (
    <div className="exam-portal-page">
      <header className="exam-simple-header">
        <div className="exam-simple-header-inner">
          <div className="exam-simple-brand-wrap">
            <img
              src="/logo192.png"
              alt="SM-ART"
              className="exam-simple-brand-logo"
            />
            <div>
              <div className="exam-simple-brand-title">SM-ART</div>
              <div className="exam-simple-brand-sub">
                Online Assessment Portal
              </div>
            </div>
          </div>
          <div className="exam-header-user">
            <div className="exam-header-user-copy">
              <strong>{employeeName}</strong>
              <span>User ID: {employeeCode}</span>
            </div>
            <Button icon={<LogoutOutlined />} onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </div>
      </header>
      <main className="exam-home-container">
        <section className="exam-home-intro">
          <div className="exam-section-label">
            <ReadOutlined />
            Employee Assessment
          </div>
          <Title level={2}>Welcome, {employeeName}</Title>
          <Text>
            Review your assigned assessment and begin when you are ready.
          </Text>
        </section>
        <section className="exam-inline-profile exam-home-surface">
          <div>
            <span>Employee</span>
            <strong>{employeeName}</strong>
          </div>
          <div>
            <span>User ID</span>
            <strong>{employeeCode}</strong>
          </div>
          <div>
            <span>Department</span>
            <strong>{departmentName}</strong>
          </div>
          <div>
            <span>Unit</span>
            <strong>
              {unitName} {unitCode ? `(${unitCode})` : ""}
            </strong>
          </div>
        </section>
        {exams.length === 0 ? (
          <section className="exam-empty-simple">
            <h2>No Assessment Available</h2>
            <p>
              There is currently no active assessment assigned to your
              department.
            </p>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadActiveExam}
              className="exam-outline-button"
            >
              Check Again
            </Button>
          </section>
        ) : (
          <section className="exam-assessment-simple exam-home-surface">
            <div className="exam-level-select-row">
              <span className="exam-level-prompt">Choose assessment level</span>
              <div className="exam-level-options" role="group" aria-label="Assessment level">
                {Array.from(new Map(
                  exams.map((item) => [item.level, item])
                ).values()).sort((a, b) => {
                  const aLevel = Number(String(a.level || "").match(/\d+/)?.[0] || 99);
                  const bLevel = Number(String(b.level || "").match(/\d+/)?.[0] || 99);
                  return aLevel - bLevel;
                }).map((item, index) => {
                  const isSelected = item.level === selectedLevel;
                  const normalizedLevel = String(item.level || "").toLowerCase();
                 const difficultyLabel =
  normalizedLevel === "level_1"
    ? "Level 1"
    : normalizedLevel === "level_2"
      ? "Level 2"
      : normalizedLevel === "level_3"
        ? "Level 3"
        : formatLevel(item.level);
                  const levelNumber =
                    normalizedLevel.match(/\d+/)?.[0] || String(index + 1);
                  return (
                    <Button
                      key={item.level}
                      type="default"
                      aria-pressed={isSelected}
                      className={`exam-level-option ${normalizedLevel.replaceAll("_", "-")}${isSelected ? " is-selected" : ""}`}
                      onClick={() => setSelectedLevel(item.level)}
                    >
                      <span className="exam-level-option-number">
                        {String(levelNumber).padStart(2, "0")}
                      </span>
                      <span>{difficultyLabel}</span>
                    </Button>
                  );
                })}
              </div>
            </div>
            {exam && (
              <div className="exam-selected-assessment">
                <div className="exam-assessment-heading-row">
              <div className="exam-status-line">
                <span className="exam-status-dot" />
                Active Assessment
                {Number(exam.submitted_attempts || 0) > 0 && (
                  <span className="exam-attempt-text">
                    Attempted {exam.submitted_attempts} time
                    {Number(exam.submitted_attempts) > 1 ? "s" : ""}
                  </span>
                )}
              </div>
              <Title level={3}>{exam.exam_name}</Title>
              <Text>
                Complete the assessment within the time limit and submit to
                view your score.
              </Text>
            </div>
            <div className="exam-inline-stats">
              <div>
                <span>Questions</span>
                <strong>{exam.question_count}</strong>
              </div>
              <div>
                <span>Total Marks</span>
                <strong>{exam.question_count}</strong>
              </div>
              <div>
                <span>Time Limit</span>
                <strong>{exam.duration_minutes || 30} min</strong>
              </div>
            </div>
            <div className="exam-simple-divider" />
            <div className="exam-instructions-simple">
              <h4>Instructions</h4>
              <ul>
                <li>The timer starts when you begin the assessment.</li>
                <li>Read each question carefully before selecting your answer.</li>
                <li>You can move between questions using Previous, Next, or question numbers.</li>
                <li>At 00:00, your current answers are submitted automatically.</li>
                <li>The result page shows only your score summary.</li>
              </ul>
            </div>
            <div className="exam-home-actions">
              <Button
                type="primary"
                size="large"
                loading={starting}
                icon={<PlayCircleOutlined />}
                onClick={handleStartExam}
                className="exam-gradient-button exam-assessment-start-button"
              >
                {Number(exam.submitted_attempts || 0) > 0
                  ? "Retake Assessment"
                  : "Start Assessment"}
              </Button>
              <span>Start when you are ready.</span>
            </div>
              </div>
            )}
          </section>
        )}
        <section className="exam-history-section exam-home-surface">
          <div className="exam-history-header">
            <div>
              <div className="exam-section-label">
                <HistoryOutlined />
                Assessment History
              </div>
              <h3>My Attempt Summary</h3>
              <p>
                Review your completed assessment attempts and scores.
              </p>
            </div>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadAttemptHistory}
              loading={historyLoading}
              className="exam-history-refresh"
            >
              Refresh
            </Button>
          </div>
          {historyLoading ? (
            <div className="exam-history-loading">
              <Spin size="small" />
              <span>Loading attempts...</span>
            </div>
          ) : attemptHistory.length === 0 ? (
            <div className="exam-history-empty">
              No completed attempts yet.
            </div>
          ) : (
            <div className="exam-history-list">
              <div className="exam-history-table-head">
                <span>Assessment</span>
                <span>Attempt</span>
                <span>Score</span>
                <span>Correct</span>
                <span>Wrong</span>
                <span>Unanswered</span>
                <span>Marks</span>
                <span>Date</span>
                <span />
              </div>
              {attemptHistory.map((item) => (
                <div
                  className="exam-history-row"
                  key={item.attempt_id}
                >
                  <div className="exam-history-exam">
                    <strong>{item.exam_name}</strong>
                    <span>{formatLevel(item.level)} · Exam ID: {item.exam_id}</span>
                  </div>
                  <div className="exam-history-cell">
                    <span className="exam-history-mobile-label">
                      Attempt
                    </span>
                    <strong>{item.attempt_no}</strong>
                  </div>
                  <div className="exam-history-cell">
                    <span className="exam-history-mobile-label">
                      Score
                    </span>
                    <strong className="exam-history-score">
                      {Number(item.percentage || 0)}%
                    </strong>
                  </div>
                  <div className="exam-history-cell">
                    <span className="exam-history-mobile-label">
                      Correct
                    </span>
                    <strong className="exam-history-correct">
                      {item.correct_count}/{item.total_questions}
                    </strong>
                  </div>
                  <div className="exam-history-cell">
                    <span className="exam-history-mobile-label">
                      Wrong
                    </span>
                    <strong>{item.wrong_count}</strong>
                  </div>
                  <div className="exam-history-cell">
                    <span className="exam-history-mobile-label">
                      Unanswered
                    </span>
                    <strong>{item.unanswered_count}</strong>
                  </div>
                  <div className="exam-history-cell">
                    <span className="exam-history-mobile-label">
                      Marks
                    </span>
                    <strong>
                      {item.obtained_marks}/{item.total_marks}
                    </strong>
                  </div>
                  <div className="exam-history-cell exam-history-date">
                    <span className="exam-history-mobile-label">
                      Date
                    </span>
                    <strong>{formatAttemptDate(item.submitted_at)}</strong>
                  </div>
                  <Button
                    size="small"
                    icon={<EyeOutlined />}
                    className="exam-history-view"
                    onClick={() => {
                      window.location.href =
                        `/exam/result/${item.attempt_id}`;
                    }}
                  >
                    View
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
      <footer className="exam-footer">
        SM-ART Smart Manufacturing • Assessment Portal
      </footer>
    </div>
  );
}
